import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Modal, Form, Input, DatePicker, Select, InputNumber, 
  Button, message, Spin, Row, Col, Typography, Divider, 
  Table, Tag, Alert 
} from 'antd';
import { MinusCircleOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createCycle, updateCycle, fetchCycles } from '../../redux/slices/planning/planningThunks';
import { getAllDelegatesList } from '../../services/delegateService';
import { getAssignedPersons } from '../../redux/slices/persons/personsThunk';
import { checkDoctorAssignmentConflicts } from '../../services/planning.service';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';
const MAX_WEEKS = 4;

function CycleFormModal({ visible, onCancel, cycle }) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isEditing = !!cycle;

  const selectedDelegateId = Form.useWatch('delegate_id', form);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [delegates, setDelegates] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(false);
  const [assignableDoctors, setAssignableDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // --- ÉTAT DU CONFLIT INSTANTANÉ (LORS DE LA SÉLECTION D'UN MÉDECIN) ---
  const [instantConflictModalVisible, setInstantConflictModalVisible] = useState(false);
  const [instantConflictDetails, setInstantConflictDetails] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  // --- ÉTAT DU CONFLIT GLOBAL (LORS DU CLIC SUR ENREGISTRER) ---
  const [globalConflictModalVisible, setGlobalConflictModalVisible] = useState(false);
  const [globalConflictsList, setGlobalConflictsList] = useState([]);
  const [pendingValuesToSend, setPendingValuesToSend] = useState(null);

  // Filtres
  const [regionFilter, setRegionFilter] = useState(null);
  const [specialtyFilter, setSpecialtyFilter] = useState(null);
  const [potentialFilter, setPotentialFilter] = useState(null);

  const [regionFilterOptions, setRegionFilterOptions] = useState([]);
  const [specialtyFilterOptions, setSpecialtyFilterOptions] = useState([]);
  const [potentialFilterOptions, setPotentialFilterOptions] = useState([]);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setAssignableDoctors([]);
      setRegionFilter(null);
      setSpecialtyFilter(null);
      setPotentialFilter(null);
      setInstantConflictModalVisible(false);
      setInstantConflictDetails(null);
      setGlobalConflictModalVisible(false);
      setGlobalConflictsList([]);
      setPendingValuesToSend(null);

      if (!isEditing) {
        form.setFieldsValue({ target_visits_per_week: 1, weeks: [{}] });
      }
      setLoadingDelegates(true);
      getAllDelegatesList()
        .then(response => {
          if (!isMounted.current) return;
          setDelegates(response?.data || []);
          if (isEditing && cycle) {
            const weeksFromApi = cycle.weeks || [];
            const sortedWeeks = [...weeksFromApi].sort((a, b) => a.week_number - b.week_number);
            
            form.setFieldsValue({
              delegate_id: cycle.delegate?.id,
              name: cycle.name,
              target_visits_per_week: cycle.target_visits_per_week,
              weeks: sortedWeeks.map(w => ({
                dates: [dayjs(w.start_date), dayjs(w.end_date)],
                personIds: w.persons?.map(p => p.id) || []
              }))
            });
          }
        })
        .catch(() => { if (isMounted.current) message.error('Erreur chargement délégués.'); })
        .finally(() => { if (isMounted.current) setLoadingDelegates(false); });
    }
  }, [visible, isEditing, cycle, form]);

  useEffect(() => {
    if (typeof selectedDelegateId === 'number' && !isNaN(selectedDelegateId)) {
      setLoadingDoctors(true);
      setAssignableDoctors([]);
      dispatch(getAssignedPersons({ 
        delegateId: selectedDelegateId, 
        page: 1, 
        limit: 500,
        typeIds: [1],
        filter: { type: [1] } 
      }))
        .unwrap()
        .then((response) => {
          if (!isMounted.current) return;
          setAssignableDoctors(response?.data || []);
        })
        .catch(() => { if (isMounted.current) message.error(`Erreur chargement médecins.`); })
        .finally(() => { if (isMounted.current) setLoadingDoctors(false); });
    } else {
      setAssignableDoctors([]);
    }
  }, [selectedDelegateId, dispatch]);

  useEffect(() => {
    if (assignableDoctors.length > 0) {
      const regionMap = new Map();
      const specialtyMap = new Map();
      const potentialSet = new Set();
      assignableDoctors.forEach(doc => {
        if (doc.region?.id != null && doc.region?.name) regionMap.set(doc.region.id, doc.region.name);
        if (doc.doctor?.specialty?.id != null && doc.doctor.specialty.name) specialtyMap.set(doc.doctor.specialty.id, doc.doctor.specialty.name);
        if (doc.potential != null && String(doc.potential).trim() !== '') potentialSet.add(String(doc.potential).trim());
      });
      setRegionFilterOptions(Array.from(regionMap, ([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label)));
      setSpecialtyFilterOptions(Array.from(specialtyMap, ([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label)));
      setPotentialFilterOptions(Array.from(potentialSet).map(pot => ({ value: pot, label: pot })).sort((a, b) => a.label.localeCompare(b.label)));
    } else {
      setRegionFilterOptions([]);
      setSpecialtyFilterOptions([]);
      setPotentialFilterOptions([]);
    }
  }, [assignableDoctors]);

  const handleDelegateChange = () => {
    form.setFieldsValue({ weeks: [{}] });
  };

  // =========================================================================
  // ⚡ VÉRIFICATION INSTANTANÉE LORSQU'ON SÉLECTIONNE UN MÉDECIN
  // =========================================================================
  const handleDoctorSelect = async (personId, weekIndex) => {
    const currentWeeks = form.getFieldValue('weeks') || [];
    const targetWeek = currentWeeks[weekIndex];

    if (!selectedDelegateId) {
      message.warning("Veuillez sélectionner un délégué d'abord.");
      return;
    }

    if (!targetWeek?.dates || targetWeek.dates.length !== 2) {
      message.info(`Renseignez la période de la Semaine ${weekIndex + 1} pour vérifier les conflits en temps réel.`);
      return;
    }

    const startDateStr = targetWeek.dates[0].format(dateFormat);
    const endDateStr = targetWeek.dates[1].format(dateFormat);

    setCheckingConflict(true);
    try {
      const res = await checkDoctorAssignmentConflicts({
        delegate_id: selectedDelegateId,
        cycle_id: isEditing ? cycle?.id : undefined,
        weeks: [{
          start_date: startDateStr,
          end_date: endDateStr,
          personIds: [personId]
        }]
      });

      if (res.data?.hasConflict && res.data?.conflicts?.length > 0) {
        const conflict = res.data.conflicts[0];
        setInstantConflictDetails({
          ...conflict,
          weekIndex,
          personId
        });
        setInstantConflictModalVisible(true);
      }
    } catch (err) {
      console.error("Erreur lors de la vérification de conflit en direct:", err);
    } finally {
      setCheckingConflict(false);
    }
  };

  // Annuler l'ajout du médecin en conflit (le retirer du select)
  const handleCancelInstantConflict = () => {
    if (!instantConflictDetails) return;
    const { weekIndex, personId } = instantConflictDetails;
    const currentWeeks = form.getFieldValue('weeks') || [];
    const targetWeek = currentWeeks[weekIndex];

    if (targetWeek && targetWeek.personIds) {
      const updatedIds = targetWeek.personIds.filter(id => id !== personId);
      currentWeeks[weekIndex].personIds = updatedIds;
      form.setFieldsValue({ weeks: [...currentWeeks] });
    }
    setInstantConflictModalVisible(false);
    setInstantConflictDetails(null);
  };

  // Conserver le médecin malgré le conflit
  const handleKeepInstantConflict = () => {
    setInstantConflictModalVisible(false);
    setInstantConflictDetails(null);
  };

  // Sauvegarde effective
  const executeSaveCycle = (dataToSend) => {
    setSubmitLoading(true);
    const action = isEditing ? dispatch(updateCycle({ id: cycle.id, ...dataToSend })) : dispatch(createCycle(dataToSend));
    action.unwrap()
      .then(() => {
        if (!isMounted.current) return;
        setGlobalConflictModalVisible(false);
        onCancel();
        dispatch(fetchCycles());
      })
      .finally(() => { if (isMounted.current) setSubmitLoading(false); });
  };

  // Validation au clic sur Créer / Mettre à jour
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      const weeksData = (values.weeks || []).map((week) => {
        if (!week.dates || week.dates.length !== 2) {
          return { start_date: '', end_date: '', personIds: [] };
        }
        return {
          start_date: week.dates[0].format(dateFormat),
          end_date: week.dates[1].format(dateFormat),
          personIds: week.personIds || [],
        };
      });

      const dataToSend = {
        name: values.name,
        delegate_id: values.delegate_id,
        target_visits_per_week: values.target_visits_per_week,
        weeks: weeksData,
      };

      // Vérification globale avant envoi
      const conflictRes = await checkDoctorAssignmentConflicts({
        delegate_id: values.delegate_id,
        cycle_id: isEditing ? cycle.id : undefined,
        weeks: weeksData,
      });

      if (conflictRes.data?.hasConflict && conflictRes.data?.conflicts?.length > 0) {
        setGlobalConflictsList(conflictRes.data.conflicts);
        setPendingValuesToSend(dataToSend);
        setGlobalConflictModalVisible(true);
        setSubmitLoading(false);
        return;
      }

      executeSaveCycle(dataToSend);

    } catch (err) {
      if (err?.errorFields) {
        message.warning('Veuillez vérifier les champs du formulaire.');
      }
      setSubmitLoading(false);
    }
  };

  const filteredDoctorOptions = useMemo(() => {
    let doctors = assignableDoctors;
    if (regionFilter != null) {
      doctors = doctors.filter(doc => doc.region?.id === regionFilter);
    }
    if (specialtyFilter != null) {
      doctors = doctors.filter(doc => doc.doctor?.specialty?.id === specialtyFilter);
    }
    if (potentialFilter != null && potentialFilter !== '') {
      doctors = doctors.filter(doc => String(doc.potential).trim() === potentialFilter);
    }
    return doctors.map(doc => {
      const fullName = `${doc.firstname || ''} ${doc.lastname || ''}`.trim() || `ID:${doc.id}`;
      const details = [doc.doctor?.specialty?.name, doc.region?.name, doc.potential ? `Pot: ${doc.potential}` : null]
        .filter(Boolean).join(' - ');
      return {
        key: doc.id, 
        value: doc.id,
        label: details ? `${fullName} (${details})` : fullName
      };
    });
  }, [assignableDoctors, regionFilter, specialtyFilter, potentialFilter]);

  const globalConflictColumns = [
    {
      title: 'Médecin en conflit',
      dataIndex: 'personName',
      key: 'personName',
      render: (text, r) => <Typography.Text strong>{text || `ID #${r.personId}`}</Typography.Text>,
    },
    {
      title: 'Votre Semaine',
      dataIndex: 'currentWeekIndex',
      key: 'currentWeekIndex',
      render: (num) => <Tag color="blue">Semaine {num}</Tag>,
    },
    {
      title: 'Déjà assigné à',
      dataIndex: 'assignedDelegateName',
      key: 'assignedDelegateName',
      render: (text) => <Tag color="orange">{text || 'Autre délégué'}</Tag>,
    },
    {
      title: 'Cycle / Semaine en conflit',
      key: 'conflictDetails',
      render: (_, r) => (
        <span>
          <strong>{r.conflictCycleName}</strong> (S{r.conflictWeekNumber})
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {r.conflictStartDate} au {r.conflictEndDate}
          </Typography.Text>
        </span>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={isEditing ? 'Modifier le Cycle' : 'Ajouter un Cycle'}
        open={visible} onCancel={onCancel}
        footer={[
          <Button key="back" onClick={onCancel} disabled={submitLoading}>Annuler</Button>,
          <Button key="submit" type="primary" loading={submitLoading} onClick={handleOk} disabled={loadingDelegates || !selectedDelegateId}>
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        ]}
        width={900} destroyOnClose maskClosable={false}
      >
        <Spin spinning={loadingDelegates || loadingDoctors || checkingConflict} tip="Chargement des données...">
          <Form form={form} layout="vertical" name="cycle_form_modal">
            <Row gutter={16}>
              <Col xs={24} sm={12}><Form.Item name="delegate_id" label="Délégué" rules={[{ required: true, message: 'Sélectionnez un délégué!' }]}>
                  <Select placeholder="Sélectionner..." onChange={handleDelegateChange} showSearch allowClear optionFilterProp="label" options={delegates.map(d => ({ value: d.id, label: `${d.firstName || d.firstname || ''} ${d.lastName || d.lastname || ''}`.trim() }))} />
              </Form.Item></Col>
              <Col xs={24} sm={12} md={8}><Form.Item name="name" label="Nom Cycle (Optionnel)"><Input placeholder="Ex: Q3 2024..." /></Form.Item></Col>
              <Col xs={24} sm={12} md={4}><Form.Item name="target_visits_per_week" label="Objectif/Sem." rules={[{ required: true, message: 'Requis' }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            
            <Divider>Planification Hebdomadaire</Divider>

            <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <Typography.Text strong>Filtres de recherche des médecins</Typography.Text>
              <Row gutter={[16, 10]} style={{ marginTop: '10px' }}>
                <Col xs={24} md={8}>
                  <Select placeholder="Filtrer par Région" value={regionFilter} onChange={setRegionFilter} allowClear disabled={!selectedDelegateId || loadingDoctors} style={{ width: '100%' }} options={regionFilterOptions} />
                </Col>
                <Col xs={24} md={8}>
                  <Select placeholder="Filtrer par Spécialité" value={specialtyFilter} onChange={setSpecialtyFilter} allowClear disabled={!selectedDelegateId || loadingDoctors} style={{ width: '100%' }} options={specialtyFilterOptions} />
                </Col>
                <Col xs={24} md={8}>
                  <Select placeholder="Filtrer par Potentiel" value={potentialFilter} onChange={setPotentialFilter} allowClear disabled={!selectedDelegateId || loadingDoctors} style={{ width: '100%' }} options={potentialFilterOptions} />
                </Col>
              </Row>
            </div>

            <Form.List name="weeks">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} style={{ border: '1px solid #f0f0f0', padding: '16px', borderRadius: '8px', marginBottom: '16px', position: 'relative' }}>
                      <Typography.Title level={5}>Semaine {index + 1}</Typography.Title>
                      {fields.length > 1 && (
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} style={{ position: 'absolute', top: '16px', right: '16px' }} />
                      )}
                      <Form.Item
                        {...restField}
                        name={[name, 'dates']}
                        label="Période de la semaine"
                        rules={[{ required: true, message: `Période S${index + 1} requise` }]}
                      >
                        <RangePicker format={dateFormat} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'personIds']}
                        label="Médecins à visiter cette semaine"
                      >
                        <Select
                          mode="multiple"
                          placeholder={!selectedDelegateId ? "Sélectionnez d'abord un délégué" : "Choisir des médecins..."}
                          disabled={!selectedDelegateId}
                          loading={loadingDoctors}
                          allowClear showSearch optionFilterProp="label"
                          style={{ width: '100%' }}
                          listHeight={256}
                          options={filteredDoctorOptions}
                          // ⚡ DÉTECTION INSTANTANÉE LORS DE LA SÉLECTION D'UN MÉDECIN
                          onSelect={(value) => handleDoctorSelect(value, index)}
                        />
                      </Form.Item>
                    </div>
                  ))}
                  
                  {fields.length < MAX_WEEKS && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Ajouter une Semaine
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Form>
        </Spin>
      </Modal>

      {/* ========================================================================= */}
      {/* ⚡ MODAL 1 : ALERTE INSTANTANÉE LORS DE LA SAISIE D'UN MÉDECIN              */}
      {/* ========================================================================= */}
      <Modal
        title={
          <span style={{ color: '#fa8c16' }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Conflit d'assignation détecté !
          </span>
        }
        open={instantConflictModalVisible}
        onCancel={handleCancelInstantConflict}
        zIndex={1300}
        width={580}
        footer={[
          <Button key="cancel" onClick={handleCancelInstantConflict}>
            Annuler (Retirer le médecin)
          </Button>,
          <Button key="keep" type="primary" onClick={handleKeepInstantConflict}>
            Conserver quand même
          </Button>,
        ]}
      >
        {instantConflictDetails && (
          <div>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  Le médecin <strong>{instantConflictDetails.personName}</strong> est déjà assigné sur la même période à un autre délégué.
                </span>
              }
            />

            <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px solid #f0f0f0', lineHeight: 1.8 }}>
              <div>👤 <strong>Autre délégué :</strong> <Tag color="orange">{instantConflictDetails.assignedDelegateName}</Tag></div>
              <div>📅 <strong>Cycle / Semaine :</strong> {instantConflictDetails.conflictCycleName} (Semaine {instantConflictDetails.conflictWeekNumber})</div>
              <div>🗓️ <strong>Période :</strong> du {instantConflictDetails.conflictStartDate} au {instantConflictDetails.conflictEndDate}</div>
            </div>

            <p style={{ marginTop: 16, marginBottom: 0 }}>
              Souhaitez-vous <strong>conserver</strong> cette sélection ou <strong>annuler</strong> pour retirer ce médecin ?
            </p>
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* ⚠️ MODAL 2 : ALERTE GLOBALE RÉCAPITULATIVE AVANT ENREGISTREMENT           */}
      {/* ========================================================================= */}
      <Modal
        title={
          <span style={{ color: '#d46b08' }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Attention : Conflit d'assignation détecté !
          </span>
        }
        open={globalConflictModalVisible}
        onCancel={() => setGlobalConflictModalVisible(false)}
        width={750}
        zIndex={1200}
        footer={[
          <Button key="cancel" onClick={() => setGlobalConflictModalVisible(false)}>
            Retourner modifier
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={submitLoading}
            onClick={() => {
              if (pendingValuesToSend) {
                executeSaveCycle(pendingValuesToSend);
              }
            }}
          >
            Enregistrer quand même
          </Button>,
        ]}
      >
        <p>
          Certains médecins que vous tentez d'assigner sont <strong>déjà planifiés pour un autre délégué sur la même période</strong> :
        </p>
        <Table
          dataSource={globalConflictsList}
          columns={globalConflictColumns}
          rowKey={(r, idx) => `${r.personId}-${r.assignedDelegateId}-${idx}`}
          pagination={false}
          size="small"
          bordered
          style={{ marginBottom: 12 }}
        />
        <Typography.Text type="secondary">
          Souhaitez-vous quand même forcer cette assignation simultanée ou revenir en arrière pour retirer ces médecins ?
        </Typography.Text>
      </Modal>
    </>
  );
}

export default CycleFormModal;